import * as RadixSelect from '@radix-ui/react-select'
import { ChevronDown, Check, X, Search } from 'lucide-react'
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'

export interface StyledSelectOption {
  value: string | undefined
  label: string
  /**
   * Optional element shown on the right of the option in the OPEN list only
   * (e.g. a status pill). Rendered outside Radix's ItemText, so the closed
   * trigger still shows just `label`, and search still matches on `label`.
   */
  adornment?: React.ReactNode
}

interface StyledSelectProps {
  value?: string
  /** Preferred Radix-style callback */
  onValueChange?: (value: string) => void
  /** Back-compat: some callers use onChange */
  onChange?: (value: string) => void
  options: ReadonlyArray<StyledSelectOption>
  placeholder?: string
  className?: string
  size?: string
  active?: boolean
  onClear?: () => void
  'aria-label'?: string
  /** Show a filter box at the top of the dropdown to search long option lists. */
  searchable?: boolean
  /** Placeholder for the search box (searchable only). */
  searchPlaceholder?: string
  /**
   * Not yet selectable — typically because a parent filter has to be chosen
   * first. The placeholder should still name THIS control; the disabled state
   * is what says it is not ready, so two dependent selects do not both read
   * "Select a client first" and become indistinguishable.
   */
  disabled?: boolean
}

export function StyledSelect({
  value,
  onValueChange,
  onChange,
  options,
  placeholder = 'Select...',
  className,
  size,
  active,
  onClear,
  'aria-label': ariaLabel,
  searchable,
  searchPlaceholder = 'Search…',
  disabled,
}: StyledSelectProps) {
  const handleValueChange = onValueChange ?? onChange ?? (() => {})
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  // Reset the query each time the dropdown closes; focus the box when it opens.
  useEffect(() => {
    if (!searchable) return
    if (open) {
      const t = setTimeout(() => searchRef.current?.focus(), 20)
      return () => clearTimeout(t)
    }
    setQuery('')
  }, [open, searchable])
  /** Radix Select requires unique `value`s; callers sometimes pass duplicates (e.g. bad API rows). */
  const safeOptions = useMemo(() => {
    const out: { value: string; label: string; adornment?: React.ReactNode }[] = []
    const seen = new Set<string>()
    for (const opt of options) {
      if (typeof opt.value !== 'string' || opt.value.length === 0) continue
      if (seen.has(opt.value)) continue
      seen.add(opt.value)
      const label = typeof opt.label === 'string' ? opt.label : String(opt.label ?? '')
      out.push({ value: opt.value, label, adornment: opt.adornment })
    }
    return out
  }, [options])
  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return safeOptions
    const q = query.trim().toLowerCase()
    return safeOptions.filter((o) => o.label.toLowerCase().includes(q))
  }, [safeOptions, query, searchable])
  const contentClass = size === 'sm' ? 'styled-select-content styled-select-content--sm' : 'styled-select-content'
  const activeStyle: React.CSSProperties = active
    ? { borderColor: 'var(--primary)', background: 'color-mix(in srgb, var(--primary) 8%, #fff)', color: 'var(--primary)', fontWeight: 600 }
    : {}
  const showClear = !!onClear && !!value
  return (
    <div style={{ position: 'relative' }}>
      <RadixSelect.Root
        disabled={disabled}
        value={value ?? ''}
        onValueChange={handleValueChange}
        {...(searchable ? { open, onOpenChange: setOpen } : {})}
      >
        <RadixSelect.Trigger
          className={`styled-select-trigger ${className ?? ''}`}
          aria-label={ariaLabel}
          disabled={disabled}
          style={
            disabled
              ? { ...activeStyle, opacity: 0.55, cursor: 'not-allowed' }
              : activeStyle
          }
        >
          <RadixSelect.Value placeholder={placeholder} />
          {/* Hide chevron (keep space) when × replaces it */}
          <RadixSelect.Icon className="styled-select-icon" style={showClear ? { opacity: 0, pointerEvents: 'none' } : undefined}>
            <ChevronDown size={size === 'sm' ? 12 : 14} />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>

        <RadixSelect.Portal>
          {/* avoidCollisions defaults to true — Radix prefers `side="bottom"` and
              auto-flips to `top` when there isn't enough room below the trigger
              (e.g. page-size dropdown at the bottom of a tall table). Explicit
              `false` previously forced the menu off-screen on /workspace/queue,
              /workspace/history, audit tabs etc. */}
          <RadixSelect.Content
            className={contentClass}
            position="popper"
            side="bottom"
            sideOffset={4}
            align="start"
          >
            {searchable && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
                  borderBottom: '1px solid var(--border-subtle)', position: 'sticky', top: 0,
                  background: 'var(--surface-1, #fff)', zIndex: 1,
                }}
                // Radix Select intercepts keystrokes for typeahead; keep them in
                // the input (Escape still bubbles so the dropdown can close).
                onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation() }}
              >
                <Search size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)' }}
                />
              </div>
            )}
            <RadixSelect.Viewport className="styled-select-viewport">
              {filtered.map((opt, index) => (
                <SelectItem key={`${opt.value}:${index}`} value={opt.value} adornment={opt.adornment}>
                  {opt.label}
                </SelectItem>
              ))}
              {searchable && filtered.length === 0 && (
                <div style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-muted)' }}>No matches</div>
              )}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
      {showClear && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClear!() }}
          aria-label="Clear selection"
          style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: 'var(--text-muted)', zIndex: 1, borderRadius: 3 }}
        >
          <X size={size === 'sm' ? 12 : 14} />
        </button>
      )}
    </div>
  )
}

const SelectItem = forwardRef<
  HTMLDivElement,
  RadixSelect.SelectItemProps & { children: React.ReactNode; adornment?: React.ReactNode }
>(
  ({ children, adornment, ...props }, ref) => (
    <RadixSelect.Item className="styled-select-item" {...props} ref={ref}>
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
      {adornment != null && (
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
          {adornment}
        </span>
      )}
      <RadixSelect.ItemIndicator className="styled-select-indicator">
        <Check size={12} />
      </RadixSelect.ItemIndicator>
    </RadixSelect.Item>
  )
)
SelectItem.displayName = 'SelectItem'
